import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Rocket } from 'lucide-react';
import { Container, PageHeader, SplitView } from '@vnetwork/ui/layout';
import { Input, Skeleton } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useInstanceConfig,
  useImages,
  useCreateInstance,
  useSshKeys,
  useMarketplaces,
  useQuotaValidator,
  type ConfigImage,
  type MarketplaceApp,
} from '@/api/opennebula';
import { groupByDistro, SectionCard, type CreateState } from './instance-create/shared';
import { OperatingSystemCard } from './instance-create/OsSection';
import { ResourceConfigCard } from './instance-create/ResourceSection';
import { VolumeCard, NetworkCard, FirewallCard } from './instance-create/StorageNetworkSection';
import { AuthCard, TagsCard } from './instance-create/AuthTagsSection';
import { SummaryRail } from './instance-create/SummaryRail';
import { buildSecgroupPayload } from './instance-create/secgroup-payload';
import styles from './InstanceCreate.module.css';

// Stable anchor ids for each create-form section, in the order they render.
// A failed submit scrolls to (and rings) the first section that has an error.
const SEC = {
  name: 'create-sec-name',
  os: 'create-sec-os',
  resource: 'create-sec-resource',
  volume: 'create-sec-volume',
  network: 'create-sec-network',
  auth: 'create-sec-auth',
} as const;
type SectionId = (typeof SEC)[keyof typeof SEC];

/**
 * Anchored section wrapper: `id` is a scroll target and gets a danger ring when
 * it's the section that blocked submit. Hoisted to module scope so its type ref
 * stays stable across the parent's per-keystroke re-renders — otherwise React
 * would remount the whole subtree and the name Input would lose focus. The `key`
 * changes only when `flashNonce` bumps, so the ring animation replays on each
 * failed submit without remounting on ordinary edits.
 */
function Sec({
  id,
  badSection,
  flashNonce,
  children,
}: {
  id: SectionId;
  badSection: SectionId | null;
  flashNonce: number;
  children: React.ReactNode;
}) {
  const invalid = badSection === id;
  return (
    <div
      key={invalid ? `${id}-${flashNonce}` : id}
      id={id}
      className={`${styles.section} ${invalid ? styles.invalid : ''}`}
    >
      {children}
    </div>
  );
}

export function InstanceCreate() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const base = `/services/compute/${serviceId}`;

  const { data: config, isLoading: cfgLoading } = useInstanceConfig(serviceId);
  const { data: customImages = [] } = useImages(serviceId);
  const { data: apps = [] } = useMarketplaces(serviceId);
  const { data: sshKeys = [] } = useSshKeys(serviceId);

  const distributions = config?.images ?? [];
  const flavors = config?.flavors ?? [];
  const gpuModels = config?.gpuModels ?? [];
  const secgroups = config?.secgroups ?? [];

  const [form, setForm] = useState<CreateState>({
    name: '',
    osTab: 'distributions',
    image: '',
    marketplaceApp: '',
    appFields: {},
    flavorType: 'standard',
    gpuEnabled: false,
    gpuModel: '',
    flavor: '',
    volumes: [{ name: 'system', size: 20, type: 'hiops' }],
    networks: [],
    secgroups: [],
    auth: 'sshkey',
    sshkey: '',
    password: '',
    tags: [],
  });
  const patch = (p: Partial<CreateState>) => setForm((f) => ({ ...f, ...p }));

  // Seed from the backend `initialize` payload + sensible first selections.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded || !config) return;
    const init = config.initialize;
    const firstImage = distributions[0];
    // Legacy concept: a new instance defaults to the public network (public IP).
    const publicNet = config.networks?.find((n) => n.type === 'public' || /public/i.test(n.name ?? ''));
    const defaultNetworks = publicNet ? [publicNet] : init?.networks ?? [];
    setForm((f) => ({
      ...f,
      name: f.name || init?.name || '',
      image: f.image || init?.image || (firstImage ? String(firstImage.id) : ''),
      flavor: f.flavor || init?.flavor || flavors.find((x) => x.type === 'standard')?.name || flavors[0]?.name || '',
      networks: f.networks.length ? f.networks : defaultNetworks,
      volumes:
        init?.volumes?.length
          ? init.volumes
          : firstImage?.volume
            ? [{ name: 'system', size: firstImage.volume, type: 'hiops' }]
            : f.volumes,
    }));
    setSeeded(true);
  }, [config, seeded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Derived selections -------------------------------------------------
  const selectedApp = useMemo(() => apps.find((a) => a.name === form.marketplaceApp), [apps, form.marketplaceApp]);
  const isMarketplace = form.osTab === 'marketplace';
  const appFieldDefs = selectedApp?.metadata?.fields ?? [];

  const distroGroups = useMemo(() => groupByDistro(distributions), [distributions]);
  const selectedImage = useMemo(
    () =>
      distributions.find((i) => String(i.id) === form.image) ??
      customImages.find((i) => String(i.id) === form.image),
    [distributions, customImages, form.image],
  );
  const activeDistro = selectedImage?.distro ?? distroGroups[0]?.distro ?? '';

  const visibleFlavors = useMemo(
    () => flavors.filter((f) => (form.gpuEnabled ? f.type === 'gpu' : f.type === form.flavorType)),
    [flavors, form.gpuEnabled, form.flavorType],
  );
  const selectedFlavor = flavors.find((f) => f.name === form.flavor);

  // ---- Quota --------------------------------------------------------------
  const totalVolume = form.volumes.reduce((s, v) => s + (Number(v.size) || 0), 0);
  const quota = useQuotaValidator(serviceId, {
    cores: selectedFlavor?.cores ?? 0,
    memory: selectedFlavor?.memory ?? 0,
    volumes: totalVolume,
    instances: 1,
  });
  const overQuota = !!selectedFlavor && quota.invalid;

  const create = useCreateInstance(serviceId!, {
    onSuccess: (resp) => {
      const id = (resp as { data?: { instance?: { id?: string } } })?.data?.instance?.id;
      show({ tone: 'success', title: 'Instance creation started' });
      navigate(id ? `${base}/instances/${id}` : `${base}/instances`);
    },
    onError: (e) => show({ tone: 'danger', title: (e as Error)?.message || 'Create failed' }),
  });

  // ---- Validation + submit ------------------------------------------------
  const problems = (): string[] => {
    const out: string[] = [];
    if (!form.name.trim()) out.push('Give the instance a name.');
    if (isMarketplace ? !form.marketplaceApp : !form.image) out.push('Choose an operating system.');
    if (!form.flavor) out.push('Choose an instance type.');
    if (!form.networks.length) out.push('Attach at least one network.');
    if (form.auth === 'password' ? form.password.length < 8 : !form.sshkey)
      out.push(form.auth === 'password' ? 'Password must be ≥ 8 characters.' : 'Select an SSH key.');
    if (form.volumes.some((v) => !v.size || v.size <= 0)) out.push('Every volume needs a size.');
    appFieldDefs.forEach((fd) => {
      if (fd.required !== false && fd.type !== 'select' && !(form.appFields[fd.key] ?? fd.value))
        out.push(`“${fd.key}” is required.`);
    });
    return out;
  };
  const [showErrors, setShowErrors] = useState(false);
  const [badSection, setBadSection] = useState<SectionId | null>(null);
  // Bumped on every failed submit so the highlight animation replays even when
  // the same section fails twice in a row (changing the wrapper key remounts it).
  const [flashNonce, setFlashNonce] = useState(0);
  const issues = problems();

  // First section (top-to-bottom) that fails, so submit can jump there.
  const firstBadSection = (): SectionId | null => {
    if (!form.name.trim()) return SEC.name;
    if (isMarketplace ? !form.marketplaceApp : !form.image) return SEC.os;
    if (!form.flavor) return SEC.resource;
    if (form.volumes.some((v) => !v.size || v.size <= 0)) return SEC.volume;
    if (!form.networks.length) return SEC.network;
    if (form.auth === 'password' ? form.password.length < 8 : !form.sshkey) return SEC.auth;
    if (appFieldDefs.some((fd) => fd.required !== false && fd.type !== 'select' && !(form.appFields[fd.key] ?? fd.value)))
      return SEC.os;
    return null;
  };

  const submit = () => {
    setShowErrors(true);
    const target = overQuota ? SEC.resource : firstBadSection();
    if (issues.length || overQuota) {
      if (target) {
        setBadSection(target);
        setFlashNonce((n) => n + 1);
        document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
    setBadSection(null);
    const fields = appFieldDefs.map((fd) => ({ key: fd.key, value: form.appFields[fd.key] ?? fd.value ?? '' }));
    const tagsObj = Object.fromEntries(form.tags.filter((t) => t.key).map((t) => [t.key, t.value]));
    create.mutate({
      instance: {
        name: form.name.trim(),
        image: form.image,
        flavor: form.flavor,
        auth: form.auth,
        ...(form.auth === 'password' ? { password: form.password } : { sshkey: form.sshkey }),
        networks: form.networks,
        volumes: form.volumes,
        // Always send a `secgroup` rule-object plus, when groups are attached,
        // `secgroups` as rule-objects (never bare ids) — the backend iterates them
        // doing `sg[:key]`, so a string element throws Symbol-into-Integer.
        ...buildSecgroupPayload(form.secgroups, secgroups),
        ...(form.gpuEnabled && form.gpuModel ? { gpu: { type: form.gpuModel } } : {}),
        metadata: {
          ...(Object.keys(tagsObj).length ? { tags: tagsObj } : {}),
          ...(isMarketplace && selectedApp ? { application: { name: selectedApp.name, fields } } : {}),
        },
      },
    } as unknown as Record<string, unknown>);
  };

  function pickImage(img: ConfigImage) {
    setForm((f) => ({
      ...f,
      image: String(img.id),
      volumes: f.volumes.map((v, i) =>
        i === 0 && img.volume ? { ...v, size: Math.max(v.size, img.volume) } : v,
      ),
    }));
  }
  function pickDistro(distro: string) {
    const grp = distroGroups.find((g) => g.distro === distro);
    if (grp?.images[0]) pickImage(grp.images[0]);
  }
  function pickApp(app: MarketplaceApp) {
    const img = app.images?.[0];
    patch({
      marketplaceApp: app.name,
      image: img ? String(img.id) : '',
      appFields: Object.fromEntries((app.metadata?.fields ?? []).map((fd) => [fd.key, fd.value ?? ''])),
    });
  }

  if (cfgLoading) {
    return (
      <Container size="lg">
        <PageHeader
          icon={<Rocket size={22} />}
          title="Create Instance"
          description="Instances are virtual machines you can set up in seconds — standalone or as part of a larger cloud infrastructure."
        />
        <Skeleton style={{ height: 420 }} />
      </Container>
    );
  }

  return (
    <Container size="lg">
      <PageHeader
        icon={<Rocket size={22} />}
        title="Create Instance"
        description="Instances are virtual machines you can set up in seconds — standalone or as part of a larger cloud infrastructure."
      />

      <SplitView
        asideWidth="360px"
        aside={
          <div className={styles.rail}>
            <SummaryRail
              name={form.name}
              selectedImageName={isMarketplace ? selectedApp?.name : selectedImage?.name}
              selectedFlavor={selectedFlavor}
              flavor={form.flavor}
              volumes={form.volumes}
              overQuota={overQuota}
              remaining={quota.remaining}
              issues={issues}
              showErrors={showErrors}
              pending={create.isPending}
              onCancel={() => navigate(`${base}/instances`)}
              onSubmit={submit}
            />
          </div>
        }
      >
        <div className={styles.form}>
          <Sec id={SEC.name} badSection={badSection} flashNonce={flashNonce}>
            <SectionCard
              title="Instance Information"
              description="Give your instance an identifying name you will remember them by."
            >
              <Field label="Instance name" required>
                {(p) => (
                  <Input
                    {...p}
                    value={form.name}
                    onChange={(e) => patch({ name: e.target.value })}
                    placeholder="my-instance"
                    style={{ maxWidth: 420 }}
                    invalid={showErrors && !form.name.trim()}
                  />
                )}
              </Field>
            </SectionCard>
          </Sec>

          <Sec id={SEC.os} badSection={badSection} flashNonce={flashNonce}>
            <OperatingSystemCard
              form={form}
              patch={patch}
              distroGroups={distroGroups}
              distributions={distributions}
              customImages={customImages}
              apps={apps}
              activeDistro={activeDistro}
              selectedApp={selectedApp}
              appFieldDefs={appFieldDefs}
              pickImage={pickImage}
              pickDistro={pickDistro}
              pickApp={pickApp}
            />
          </Sec>

          <Sec id={SEC.resource} badSection={badSection} flashNonce={flashNonce}>
            <ResourceConfigCard form={form} patch={patch} visibleFlavors={visibleFlavors} gpuModels={gpuModels} />
          </Sec>

          <Sec id={SEC.volume} badSection={badSection} flashNonce={flashNonce}>
            <VolumeCard volumes={form.volumes} onChange={(volumes) => patch({ volumes })} />
          </Sec>

          <Sec id={SEC.network} badSection={badSection} flashNonce={flashNonce}>
            <NetworkCard
              serviceId={serviceId}
              all={config?.networks ?? []}
              selected={form.networks}
              onChange={(networks) => patch({ networks })}
            />
          </Sec>

          {secgroups.length > 0 && (
            <FirewallCard
              secgroups={secgroups}
              selected={form.secgroups}
              onChange={(s) => patch({ secgroups: s })}
            />
          )}

          <Sec id={SEC.auth} badSection={badSection} flashNonce={flashNonce}>
            <AuthCard form={form} patch={patch} sshKeys={sshKeys} showErrors={showErrors} />
          </Sec>

          <TagsCard tags={form.tags} onChange={(tags) => patch({ tags })} />
        </div>
      </SplitView>
    </Container>
  );
}
